import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useOutletContext, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useIntersection } from 'react-use';
import { Alert, Button, cn } from '@c2fo/liquidity';
import { ChevronDownIcon } from '@c2fo/liquidity/icons';
import { useFinanceIqEnabled } from '@/enterprise/data/useFinanceIqEnabled';
import usePlayableInvoiceStats from '@/enterprise/data/usePlayableInvoiceStats';
import useTakerMarkets, { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { OfferBuilderNavigationState } from '@/enterprise/pages/OfferBuilder';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import { IntercomDataTargetNames } from '@/reporting/intercomDataTargetNames';
import { EnterpriseTrackEventNames } from '@/reporting/trackEventNames';
import { ButtonLink } from '@/shared/components/ButtonLink';
import { IconButtonLink } from '@/shared/components/IconButtonLink';
import LinkTabs from '@/shared/components/LinkTabs';
import NavigateWithRedirect from '@/shared/components/NavigateWithRedirect';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import { useReadOnlyUser } from '@/utils/useRestrictions';
import BuyerLogo from '../BuyerLogo';
import NoDataState from '../NoDataState';
import NavigationBar from '../OfferBuilder/NavigationBar';
import PageBottomPortal from '../PageBottomPortal/PageBottomPortal';
import Skeleton from '../Skeleton';
import DownloadInvoices from './DownloadInvoices';
import InvoiceCentralStats from './InvoiceCentralStats';
import useGroupByBuyerCurrency, { BuyerCurrencyGroupWithStats } from './useGroupByBuyerCurrency';
import { formatBuyerName } from './utils';

export interface InvoiceCentralLayoutContext {
  // current selected buyer
  readonly buyer: BuyerCurrencyGroupWithStats;
  // url params for the current selected buyer currency combo
  readonly urlParams: {
    makerOrganizationUuid: string;
    currency: string;
  };
}

export function useInvoiceCentralLayoutContext() {
  return useOutletContext<InvoiceCentralLayoutContext>();
}

export type LinkOption = 'eligible' | 'ineligible' | 'adjustments';

interface LinkItem {
  key: LinkOption;
  label: string;
  link: LinkOption;
  matcher: LinkOption;
  trackEvent: {
    name: EnterpriseTrackEventNames;
    attributes: Record<string, string>;
  };
}

const DisabledMarketsAlert = ({
  makerOrganizationUuid,
  disabledTakerMarkets,
}: {
  makerOrganizationUuid: string;
  disabledTakerMarkets: TakerMarket[];
}) => {
  const { t } = useTranslation();
  const { data: playableInvoiceStats } = usePlayableInvoiceStats({
    makerOrganizationUuid,
    takerMarkets: disabledTakerMarkets,
  });

  if (disabledTakerMarkets.length === 0 || playableInvoiceStats?.totalCount === 0) {
    return null;
  }

  return (
    <div className="px-6 pt-8">
      <Alert
        description={t('invoiceCentral.closedMarket.description')}
        full
        title={t('core.marketClosed')}
        type="warning"
        variant="outlined"
      />
    </div>
  );
};

const InvoiceCentralLayoutComponent = () => {
  const params = useParams<{ makerOrganizationUuid: string; currency: string }>();
  const { makerOrganizationUuid = '', currency = '' } = params;
  const { pathname } = useLocation();
  const { data: takerMarkets } = useTakerMarkets();
  const buyers = useGroupByBuyerCurrency({ takerMarkets, includeAwardedInvoices: false });
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const { addAction } = useDataDog();
  const readOnlyUser = useReadOnlyUser();
  const enableFinanceIQ = useFinanceIqEnabled();
  const intersectionRef = useRef<HTMLDivElement>(null);
  const intersection = useIntersection(intersectionRef, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  });

  const isAboveViewport = !!intersection && !intersection.isIntersecting && intersection.boundingClientRect.top < 0;

  const appDomain = (import.meta.env.VITE_LOGIN_URL ?? '').replace('/login', '');

  const noBuyersFound = !buyers || buyers.length === 0;

  useEffect(() => {
    if (noBuyersFound) {
      trackEnterpriseEvent('invoice-central::navigation::changed', {
        message: 'No buyers found for user',
      });
    }
  }, [noBuyersFound, trackEnterpriseEvent]);

  if (noBuyersFound) {
    return <NavigateWithRedirect to={asEnterpriseLink()} />;
  }

  const currentBuyerIndex = buyers.findIndex(
    (buyer) => buyer.makerOrganizationUuid === makerOrganizationUuid && buyer.currency === currency
  );

  if (!makerOrganizationUuid || !currency || currentBuyerIndex === -1) {
    // Useful for developer tracking but not necessary for long term metrics
    // Will happen if navigating to /invoice-central or /invoice-central/:makerOrganizationUuid/:currency where at least one param is invalid
    addAction('invoice-central::navigation::changed', {
      message: `Redirecting to makerOrganizationUuid ${buyers[0].makerOrganizationUuid} and currency ${buyers[0].currency}`,
    });

    const buyer = buyers[0];

    return (
      <NavigateWithRedirect
        to={asEnterpriseLink(`invoice-central/${buyer.makerOrganizationUuid}/${buyer.currency}/eligible`)}
      />
    );
  }

  const trackEventPayload: LinkItem['trackEvent'] = {
    name: 'invoice-central::tab-changed',
    attributes: {
      makerOrganizationUuid,
      currency,
    },
  };

  const linkItems: LinkItem[] = [
    {
      key: 'eligible',
      label: t('core.eligible'),
      link: 'eligible',
      matcher: 'eligible',
      trackEvent: {
        ...trackEventPayload,
        attributes: {
          ...trackEventPayload.attributes,
          tab: 'eligible',
        },
      },
    },
    {
      key: 'ineligible',
      label: t('core.ineligible'),
      link: 'ineligible',
      matcher: 'ineligible',
      trackEvent: {
        ...trackEventPayload,
        attributes: {
          ...trackEventPayload.attributes,
          tab: 'ineligible',
        },
      },
    },
    {
      key: 'adjustments',
      label: t('maker.adjustments'),
      link: 'adjustments',
      matcher: 'adjustments',
      trackEvent: {
        ...trackEventPayload,
        attributes: {
          ...trackEventPayload.attributes,
          tab: 'adjustments',
        },
      },
    },
  ];

  const currentBuyer = buyers[currentBuyerIndex];
  const previousBuyer = buyers[currentBuyerIndex - 1];
  const nextBuyer = buyers[currentBuyerIndex + 1];

  // Determine which invoice table is showing based on the current url i.e. /eligible, /ineligible, /adjustments
  const currentTable = linkItems.find((item) => pathname.includes(`/${item.matcher}`))?.matcher;

  const currentBuyerName = formatBuyerName({
    buyerToFormat: currentBuyer,
    buyers,
  });

  const nextBuyerName = formatBuyerName({
    buyerToFormat: nextBuyer,
    buyers,
  });

  const offerBuilderNavigationState = {
    preselectedTakerMarketIds: currentBuyer.takerMarkets.map((tm) => tm.id),
    location: 'invoice-central',
    source: 'build-offer',
    prioritizeSelected: true,
    frequency: null,
  } satisfies OfferBuilderNavigationState;

  const BuildOffer = () => (
    <ButtonLink
      className="w-full md:w-fit"
      disabled={readOnlyUser || currentBuyer.takerMarkets.every((tm) => !tm.marketIsEnabled)}
      onClick={() =>
        trackEnterpriseEvent('offer-builder::cta::clicked', {
          referrer: 'invoice-central',
        })
      }
      to={asEnterpriseLink('build-offer')}
      variant="cta"
      state={offerBuilderNavigationState}
    >
      {t('core.buildOffer')}
    </ButtonLink>
  );

  return (
    <div className="space-y-6">
      {enableFinanceIQ && (
        <Alert
          type="info"
          title={t('invoiceCentral.financeIq.title')}
          description={t('invoiceCentral.financeIq.description')}
          action={{
            text: t('invoiceCentral.financeIq.action'),
            onClick: () => {
              trackEnterpriseEvent('invoice-central::finance-iq-navigation::clicked');
              window.open(
                `${appDomain}/financeiq/?utm_partner=&utm_lang=en_US&utm_source=c2fo&utm_medium=in_app&utm_region=amer&utm_lifecycle=cross&utm_audience=na&utm_campaign=financeiq&utm_series=invoicecentral426&utm_country=us&utm_content=&utm_term=`,
                '_blank',
                'noreferrer'
              );
            },
          }}
          full
        />
      )}
      <div
        className="flex items-center justify-between gap-6 overflow-hidden pb-4"
        data-intercom-target={'invoiceCentralArrowNav' satisfies IntercomDataTargetNames}
      >
        <div className="flex items-center gap-2 overflow-hidden pt-2">
          <IconButtonLink
            className="rotate-90"
            disabled={!previousBuyer}
            icon={ChevronDownIcon}
            name="previous"
            to={asEnterpriseLink(
              `invoice-central/${previousBuyer?.makerOrganizationUuid}/${previousBuyer?.currency}/eligible`
            )}
            variant="ancillary"
          />
          <div className="flex items-center gap-2 overflow-hidden">
            <BuyerLogo
              buyerLogoUrl={currentBuyer.makerOrganizationLogo}
              buyerName={currentBuyer.makerOrganizationName}
              className="hidden lg:flex"
              size="lg"
            />
            <div className="min-w-0 font-serif text-3xl lg:text-4xl">
              <div className="-mb-1 truncate pb-1">{currentBuyerName}</div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 md:overflow-hidden">
          {nextBuyer && (
            <div className="hidden truncate text-wrap text-right text-sm text-text-secondary md:block lg:text-xl">
              <Trans
                i18nKey="core.customerInvoices"
                components={{ style: <span className="hidden lg:inline-block" /> }}
                values={{ customerName: nextBuyerName }}
              />
            </div>
          )}
          <IconButtonLink
            className="-rotate-90"
            disabled={!nextBuyer}
            icon={ChevronDownIcon}
            name="next"
            to={asEnterpriseLink(`invoice-central/${nextBuyer?.makerOrganizationUuid}/${nextBuyer?.currency}/eligible`)}
            variant="ancillary"
          />
        </div>
      </div>
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-end" ref={intersectionRef}>
        <DownloadInvoices
          currentBuyer={currentBuyer}
          makerOrganizationUuid={makerOrganizationUuid}
          currency={currency}
          currentTable={currentTable ?? 'eligible'}
        />
        <BuildOffer />
      </div>
      <div className="rounded-xl bg-white shadow-md">
        <QueryBoundaries>
          <DisabledMarketsAlert
            makerOrganizationUuid={makerOrganizationUuid}
            disabledTakerMarkets={currentBuyer.takerMarkets.filter((tm) => !tm.marketIsEnabled)}
          />
        </QueryBoundaries>
        <div className="p-6 pb-0">
          <div className="pb-4 text-lg font-medium">{t('core.summary')}</div>
          <QueryBoundaries
            LoadingComponent={() => <Skeleton className="h-24 w-full" />}
            ErrorComponent={() => (
              <div className="rounded-2xl bg-canvas p-4">
                <NoDataState
                  title={t('invoices.errorLoadingInvoices')}
                  message={t('invoices.errorLoadingInvoicesDescription')}
                  action={
                    <Button size="sm" variant="ancillary" onClick={() => window.location.reload()}>
                      {t('core.refresh')}
                    </Button>
                  }
                />
              </div>
            )}
          >
            <InvoiceCentralStats buyer={currentBuyer} makerOrganizationUuid={makerOrganizationUuid} />
          </QueryBoundaries>
          <div className="pt-8 text-lg font-medium">{t('maker.invoices')}</div>
          <LinkTabs items={linkItems} variant="secondary" />
        </div>
        <Outlet
          context={
            {
              buyer: currentBuyer,
              urlParams: { makerOrganizationUuid, currency },
            } satisfies InvoiceCentralLayoutContext
          }
        />
      </div>
      <PageBottomPortal>
        <NavigationBar
          className={cn('transition-all duration-300 ease-in-out', {
            'translate-y-full': !isAboveViewport,
            'translate-y-0': isAboveViewport,
          })}
        >
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row md:justify-end">
            <DownloadInvoices
              currentBuyer={currentBuyer}
              makerOrganizationUuid={makerOrganizationUuid}
              currency={currency}
              currentTable={currentTable ?? 'eligible'}
            />
            {!readOnlyUser && <BuildOffer />}
          </div>
        </NavigationBar>
      </PageBottomPortal>
    </div>
  );
};

const InvoiceCentralLayout = () => {
  return (
    <QueryBoundaries
      LoadingComponent={() => (
        <div className="space-y-6 rounded-md bg-gray-100 p-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-80 w-full" />
        </div>
      )}
    >
      <InvoiceCentralLayoutComponent />
    </QueryBoundaries>
  );
};

export default InvoiceCentralLayout;
